/**
 * Evidence — production page-family (WP1c / #46599, WP6h / #46632, WP6i / #46633).
 *
 * WP6h fills Evidence library + artifact detail: download manifest/content
 * where allowed, redact (or honest cited-by-receipt disable), queue
 * collection, data-action-id bindings.
 * WP6i fills Connectors: open catalog and reconnect via retained integration
 * setup, data-action-id bindings. No styleguide-only toasts.
 */

import { useEffect, useMemo, useState, type ReactNode } from "react";
import { apiCall, ApiError, useApi } from "@excellent/ui/api";
import { formatBytes, formatDateTime, formatNumber, formatRelativeTime } from "@excellent/ui";
import type {
  VerificationConnectorDto,
  VerificationEvidenceArtifactDto,
} from "@excellent/ui/api/contracts/verification-evidence";
import type { UniversalConsoleEvidenceArtifact } from "@excellent/core/verification-universal-surfaces-client";
import type { VerificationEnvironment } from "../env";
import { href } from "../nav";
import { useVerificationEnvOptional } from "../env-provider";
import {
  ActionButton,
  ButtonRow,
  Callout,
  Card,
  CardContent,
  DataTable,
  Field,
  Fields,
  FilterField,
  FilterToolbar,
  ListSearch,
  Mono,
  ScopeTabs,
  Section,
  Stat,
  StatGrid,
  ToneBadge,
  type DataTableColumn,
} from "../ui";
import {
  PageError,
  PageFrame,
  PageLink,
  PagePending,
  type VerificationDetailPageProps,
  type VerificationPageProps,
} from "./page-frame";
import { verificationCanonicalClients } from "../api/canonical-clients";
import { HEALTH_TONE, PROVIDER_LABEL } from "../../prototypes/verification/data";

export const EVIDENCE_DOWNLOAD_ACTION = "evidence.download";
export const EVIDENCE_REDACT_ACTION = "evidence.redact";
export const EVIDENCE_QUEUE_COLLECTION_ACTION = "evidence.queue-collection";
export const EVIDENCE_OPEN_ARTIFACT_ACTION = "evidence.open-artifact";
export const EVIDENCE_OPEN_CANONICAL_WORK_ACTION = "evidence.open-canonical-work";
const EVIDENCE_FILTER_ALL = "evidence.filter-all";
const EVIDENCE_FILTER_ACTIVE = "evidence.filter-active";
const EVIDENCE_FILTER_REDACTED = "evidence.filter-redacted";
const EVIDENCE_FILTER_EXPIRED = "evidence.filter-expired";
const EVIDENCE_FILTER_DELETED = "evidence.filter-deleted";
export const CONNECTORS_OPEN_CATALOG_ACTION = "connectors.open-catalog";
export const CONNECTORS_RECONNECT_ACTION = "connectors.reconnect";
export const CONNECTORS_CATALOG_HREF = "/settings/integrations";

type EvidenceFilter = "all" | "active" | "redacted" | "expired" | "deleted";

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return "Action failed.";
}

function useLastFetchedAt(data: unknown): string | null {
  const [lastFetchedAt, setLastFetchedAt] = useState<string | null>(null);
  useEffect(() => {
    if (data) setLastFetchedAt(new Date().toISOString());
  }, [data]);
  return lastFetchedAt;
}

function LastFetched({ at, stale }: { at: string | null; stale?: boolean }) {
  if (!at) return null;
  return (
    <Callout tone={stale ? "unknown" : "neutral"} title={stale ? "Showing stale data" : "Real data loaded"}>
      Last fetched {formatDateTime(at)}.
    </Callout>
  );
}

function environmentActor(environment: VerificationEnvironment): string {
  return environment === "demo" ? "demo:act_mw" : "real:act_matt";
}

function withDemoFallback(node: ReactNode): ReactNode {
  return <>{node}</>;
}

function MaybeDemo({ children }: { children: ReactNode }) {
  const existing = null;
  if (existing) return <>{children}</>;
  return withDemoFallback(children);
}

function stateTone(state: VerificationEvidenceArtifactDto["state"]): "pass" | "waived" | "neutral" | "fail" {
  if (state === "active") return "pass";
  if (state === "redacted") return "waived";
  if (state === "deleted") return "fail";
  return "neutral";
}

function connectorTone(health: VerificationConnectorDto["health"]) {
  return HEALTH_TONE[health] ?? "neutral";
}

function providerLabel(provider: VerificationConnectorDto["provider"]) {
  return PROVIDER_LABEL[provider] ?? provider;
}

function evidenceColumns(
  onNavigate: (to: string) => void,
  queueCollection: (id: string) => unknown,
  sourceName: (id: string) => string,
  demoScoped: boolean,
): DataTableColumn<VerificationEvidenceArtifactDto>[] {
  return [
    {
      key: "artifact",
      header: "Artifact",
      cellClassName: "whitespace-normal",
      render: (a) => (
        <div
          className="flex flex-col gap-0.5"
          data-artifact-id={a.id}
          data-demo-scoped={demoScoped ? "true" : undefined}
        >
          <span className="font-medium text-foreground">{a.label}</span>
          <Mono>{a.sourceKind}</Mono>
        </div>
      ),
    },
    {
      key: "source",
      header: "Source",
      render: (a) => sourceName(a.sourceId),
    },
    {
      key: "hash",
      header: "Content hash",
      render: (a) => <Mono>{a.contentHash}</Mono>,
    },
    {
      key: "state",
      header: "State",
      render: (a) => <ToneBadge tone={stateTone(a.state)}>{a.state}</ToneBadge>,
    },
    {
      key: "cited",
      header: "Cited by",
      render: (a) =>
        a.citedByReceiptIds.length === 0 ? (
          <span className="text-muted-foreground">—</span>
        ) : (
          <ToneBadge tone="accent">
            {`${a.citedByReceiptIds.length} receipt${a.citedByReceiptIds.length === 1 ? "" : "s"}`}
          </ToneBadge>
        ),
    },
    {
      key: "collected",
      header: "Collected",
      render: (a) => <Mono>{formatRelativeTime(a.collectedAt)}</Mono>,
    },
    {
      key: "actions",
      header: "Actions",
      render: (a) => (
        <ButtonRow>
          <ActionButton
            actionId={EVIDENCE_QUEUE_COLLECTION_ACTION}
            data-action-id={EVIDENCE_QUEUE_COLLECTION_ACTION}
            data-artifact-id={a.id}
            onClick={(event) => {
              event.stopPropagation();
              queueCollection(a.id);
            }}
          >
            Queue collection
          </ActionButton>
          <ActionButton
            actionId={EVIDENCE_OPEN_ARTIFACT_ACTION}
            data-action-id={EVIDENCE_OPEN_ARTIFACT_ACTION}
            variant="primary"
            data-artifact-id={a.id}
            onClick={(event) => {
              event.stopPropagation();
              onNavigate(href("evidence", a.id));
            }}
          >
            Open artifact
          </ActionButton>
          {a.collectionQueuedAt ? (
            <ToneBadge tone="accent">{`collection queued ${formatRelativeTime(a.collectionQueuedAt)}`}</ToneBadge>
          ) : null}
        </ButtonRow>
      ),
    },
  ];
}

function triggerDownload(filename: string, body: string) {
  if (typeof document === "undefined") return;
  const blob = new Blob([body], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = "noopener";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

const LIBRARY_LEDE =
  "Every artifact is recorded with a content hash and an append-only lineage. The hash names the bytes that were observed; it does not itself prove immutability, because this store holds the metadata and not the payload.";

const DETAIL_LEDE =
  "Lineage for one artifact: collector, source, observations, and the receipts that cite its hash. This projection does not contain the artifact payload.";

// Redaction copy is held as constants, not inline JSX text, so the formatter cannot
// re-wrap these sentences and silently break the phrasing the contract gate pins.
const REDACT_BLOCKED_NOTE =
  "Recording a redaction is blocked while that is true, so that artifact state does not conflict with existing receipt metadata.";

const REDACT_EFFECT_NOTE =
  "It marks this artifact redacted here and appends a lineage event. It does not establish that any payload removal would occur in the system that holds the bytes.";

const REDACTED_STATE_NOTE =
  "This artifact is marked redacted, and its content hash, size and lineage are retained. That records a deletion decision here and does not establish whether a separate payload was deleted in whatever system holds it. Manifest download remains allowed.";

const REDACTED_REPLAY_NOTE =
  "For a receipt that cited this artifact, authoritative receipt replay is unavailable unless a signed envelope was retained for it.";

/**
 * Canonical (v0 protocol) evidence projection.
 *
 * The artifacts a receipt actually cites live in admitted evidence bundles on
 * the universal console overview, not in the operational evidence store below.
 * An Evidence library that reports zero while the receipts page cites seven
 * bundles is wrong, so this section projects the canonical bundles and the
 * operational store keeps its own clearly-labelled section.
 */
const CANONICAL_LEDE =
  "Admitted artifacts from the canonical verification protocol — the bytes a signed receipt cites. Reading one proves nothing on its own; only a current prospective receipt carries a verdict.";

type CanonicalEvidenceRow = UniversalConsoleEvidenceArtifact & {
  key: string;
  workTitle: string | null;
};

const ACCESSIBILITY_TONE: Record<CanonicalEvidenceRow["accessibility"], "pass" | "fail" | "waived" | "unknown"> = {
  accessible: "pass",
  inaccessible: "fail",
  redacted: "waived",
  unknown: "unknown",
};

const FRESHNESS_TONE: Record<CanonicalEvidenceRow["freshness"], "pass" | "fail" | "unknown"> = {
  fresh: "pass",
  stale: "unknown",
  expired: "fail",
  unknown: "unknown",
};

const INDEPENDENCE_TONE: Record<CanonicalEvidenceRow["independence"], "pass" | "unknown" | "neutral"> = {
  independent: "pass",
  self_attested: "unknown",
  shared_boundary: "unknown",
  unknown: "neutral",
};

function canonicalEvidenceColumns(
  onNavigate: (to: string) => void,
): DataTableColumn<CanonicalEvidenceRow>[] {
  return [
    {
      key: "artifact",
      header: "Artifact",
      cellClassName: "whitespace-normal",
      render: (row) => (
        <div className="flex min-w-0 flex-col gap-1" data-canonical-artifact-id={row.artifactId}>
          {/* Plain language first: what was collected and for which Work. The
              protocol ids stay in the row, one disclosure away. */}
          <span className="font-medium text-foreground">{row.kind}</span>
          <span className="type-subtext text-muted-foreground">
            {row.workTitle ?? row.workItemId} · {row.mediaType} · {formatBytes(row.sizeBytes)}
          </span>
          <details>
            <summary className="cursor-pointer type-subtext text-muted-foreground">
              Reference IDs
            </summary>
            <div className="mt-1 flex min-w-0 flex-col gap-0.5 break-all">
              <Mono>Artifact {row.artifactId}</Mono>
              <Mono>Work {row.workItemId}</Mono>
            </div>
          </details>
        </div>
      ),
    },
    {
      key: "hash",
      header: "Content hash",
      cellClassName: "whitespace-normal",
      render: (row) => (
        <div className="flex min-w-0 flex-col gap-0.5 break-all">
          <Mono>{row.contentDigest}</Mono>
          <span className="type-subtext text-muted-foreground">
            {row.bytesVerified ? "Bytes verified against the digest" : "Bytes not verified"}
          </span>
        </div>
      ),
    },
    {
      key: "source",
      header: "Source",
      cellClassName: "whitespace-normal",
      render: (row) => (
        <div className="flex min-w-0 flex-col gap-0.5 break-all">
          <Mono>{row.sourceUri}</Mono>
          <span className="type-subtext text-muted-foreground">
            {row.collectionMethod} · collected by {row.collectorActorId}
          </span>
        </div>
      ),
    },
    {
      key: "state",
      header: "State",
      cellClassName: "whitespace-normal",
      render: (row) => (
        <ButtonRow>
          <ToneBadge tone={ACCESSIBILITY_TONE[row.accessibility]}>{row.accessibility}</ToneBadge>
          <ToneBadge tone={FRESHNESS_TONE[row.freshness]}>{row.freshness}</ToneBadge>
          <ToneBadge tone={INDEPENDENCE_TONE[row.independence]}>{row.independence}</ToneBadge>
          <ToneBadge tone="neutral">{row.mutability}</ToneBadge>
        </ButtonRow>
      ),
    },
    {
      key: "cited",
      header: "Cited by",
      cellClassName: "whitespace-normal",
      render: (row) =>
        row.citedByReceiptIds.length === 0 ? (
          <span className="text-muted-foreground">—</span>
        ) : (
          <div className="flex min-w-0 flex-col gap-1">
            <ToneBadge tone="accent">
              {`${row.citedByReceiptIds.length} receipt${row.citedByReceiptIds.length === 1 ? "" : "s"}`}
            </ToneBadge>
            <details>
              <summary className="cursor-pointer type-subtext text-muted-foreground">
                Receipt IDs
              </summary>
              <div className="mt-1 flex min-w-0 flex-col gap-0.5 break-all">
                {row.citedByReceiptIds.map((receiptId) => (
                  <Mono key={receiptId}>{receiptId}</Mono>
                ))}
              </div>
            </details>
          </div>
        ),
    },
    {
      key: "collected",
      header: "Collected",
      render: (row) => <Mono>{formatRelativeTime(row.observedAt)}</Mono>,
    },
    {
      key: "actions",
      header: "Actions",
      render: (row) => (
        <ButtonRow>
          <ActionButton
            actionId={EVIDENCE_OPEN_CANONICAL_WORK_ACTION}
            data-action-id={EVIDENCE_OPEN_CANONICAL_WORK_ACTION}
            data-work-item-id={row.workItemId}
            onClick={(event) => {
              event.stopPropagation();
              onNavigate(href("work-ledger", row.workItemId));
            }}
          >
            Open Work
          </ActionButton>
        </ButtonRow>
      ),
    },
  ];
}

export function EvidencePage({ onNavigate }: VerificationPageProps) {
  return (
    <MaybeDemo>
      <EvidencePageInner onNavigate={onNavigate} />
    </MaybeDemo>
  );
}

function EvidencePageInner({ onNavigate }: VerificationPageProps) {
  const env = useVerificationEnvOptional();
  const [q, setQ] = useState("");
  const [state, setState] = useState<EvidenceFilter>("all");
  const [notice, setNotice] = useState<string | null>(null);

  const environment = env?.environment ?? null;
  const ready = env?.ready ?? true;
  const artifactsApi = useApi(
    verificationCanonicalClients.artifactsList.operation,
    ready && environment ? { query: { env: environment } } : null,
  );
  const connectorsApi = useApi(
    verificationCanonicalClients.connectorsList.operation,
    ready && environment ? { query: { env: environment } } : null,
  );
  // Canonical protocol evidence is real-tenant only; the same client the Work
  // ledger reads, so both surfaces share one cache entry.
  const canonicalApi = useApi(
    verificationCanonicalClients.workLedger.operation,
    ready && environment === "real" ? { query: { env: "real" as const } } : null,
  );
  const lastFetchedAt = useLastFetchedAt(artifactsApi.data);

  const canonicalRows = useMemo<CanonicalEvidenceRow[]>(() => {
    const data = canonicalApi.data;
    if (!data) return [];
    const workTitles = new Map<string, string>();
    for (const item of [...data.current.work, ...data.incomplete.work]) {
      if (!workTitles.has(item.id)) workTitles.set(item.id, item.title);
    }
    return (data.evidenceArtifacts ?? [])
      .map((artifact) => ({
        ...artifact,
        key: `${artifact.bundleDigest}:${artifact.artifactId}`,
        workTitle: workTitles.get(artifact.workItemId) ?? null,
      }))
      .sort((a, b) => Date.parse(b.observedAt) - Date.parse(a.observedAt));
  }, [canonicalApi.data]);
  const replayProof = canonicalApi.data?.independentReplayProof ?? null;

  const catalog = artifactsApi.data?.items ?? [];
  const activeCount = catalog.filter((a) => a.state === "active").length;
  const redactedCount = catalog.filter((a) => a.state === "redacted").length;
  const expiredCount = catalog.filter((a) => a.state === "expired").length;
  const deletedCount = catalog.filter((a) => a.state === "deleted").length;
  const rows = useMemo(() => {
    let list: VerificationEvidenceArtifactDto[] = catalog;
    if (state !== "all") list = list.filter((a) => a.state === state);
    if (q.trim()) {
      const n = q.toLowerCase();
      list = list.filter(
        (a) =>
          a.label.toLowerCase().includes(n) ||
          a.sourceKind.toLowerCase().includes(n) ||
          a.contentHash.toLowerCase().includes(n) ||
          a.id.toLowerCase().includes(n),
      );
    }
    return [...list].sort((a, b) => new Date(b.collectedAt).getTime() - new Date(a.collectedAt).getTime());
  }, [catalog, q, state]);

  async function queueCollection(id: string) {
    if (!environment) return;
    setNotice(null);
    try {
      const result = await apiCall(verificationCanonicalClients.artifactsQueue.operation, {
        params: { id },
        query: { env: environment },
        body: { actorId: environmentActor(environment) },
      });
      setNotice(`Queued collection for ${result.artifact.label}.`);
      void artifactsApi.mutate();
    } catch (err) {
      setNotice(errorMessage(err));
    }
  }

  if (env && !ready) {
    return (
      <PageFrame page="evidence" wide eyebrow="Evidence" title="Evidence library" lede={LIBRARY_LEDE}>
        <PagePending title="Loading environment">Loading artifacts for the active Environment.</PagePending>
      </PageFrame>
    );
  }

  if (!environment) {
    return (
      <PageFrame page="evidence" wide eyebrow="Evidence" title="Evidence library" lede={LIBRARY_LEDE}>
        <PagePending title="Environment unavailable">Evidence is scoped to the active Environment.</PagePending>
      </PageFrame>
    );
  }

  if (artifactsApi.error || connectorsApi.error) {
    return (
      <PageFrame page="evidence" wide eyebrow="Evidence" title="Evidence library" lede={LIBRARY_LEDE}>
        <PageError
          title="Evidence failed to load"
          error={artifactsApi.error ?? connectorsApi.error}
          onRetry={() => {
            void artifactsApi.mutate();
            void connectorsApi.mutate();
          }}
        />
      </PageFrame>
    );
  }

  if (artifactsApi.isLoading || !artifactsApi.data) {
    return (
      <PageFrame page="evidence" wide eyebrow="Evidence" title="Evidence library" lede={LIBRARY_LEDE}>
        <PagePending title="Loading evidence">Loading {environment === "demo" ? "Demo" : "Product dogfood"} artifacts.</PagePending>
      </PageFrame>
    );
  }

  return (
    <PageFrame
      page="evidence"
      wide
      eyebrow="Evidence"
      title="Evidence library"
      lede={LIBRARY_LEDE}
    >
      {notice ? <Callout tone={notice.includes("failed") ? "fail" : "accent"} title="Evidence action">{notice}</Callout> : null}
      {environment === "real" ? (
        <Section title="Canonical evidence artifacts" note={CANONICAL_LEDE}>
          {canonicalApi.error ? (
            <PageError
              title="Canonical evidence unavailable"
              error={canonicalApi.error}
              onRetry={() => void canonicalApi.mutate()}
              actionId="evidence.canonical-retry"
            />
          ) : canonicalApi.isLoading && !canonicalApi.data ? (
            <PagePending title="Loading canonical evidence">
              Reading admitted evidence bundles from the verification protocol.
            </PagePending>
          ) : (
            <>
              {replayProof ? (
                <StatGrid cols={4}>
                  <Stat label="Evidence bundles" value={formatNumber(replayProof.evidenceBundleCount)} />
                  <Stat label="Admitted artifacts" value={formatNumber(canonicalRows.length)} />
                  <Stat
                    label="Independently collected"
                    value={formatNumber(replayProof.independentArtifactCount)}
                    foot={`${formatNumber(replayProof.supportingArtifactCount)} supporting only`}
                  />
                  <Stat
                    label="Bytes verified"
                    value={formatNumber(replayProof.bytesVerifiedArtifactCount)}
                    tone={replayProof.unverifiedArtifactCount === 0 ? "pass" : "unknown"}
                    foot={`${formatNumber(replayProof.unverifiedArtifactCount)} unverified`}
                  />
                </StatGrid>
              ) : null}
              <DataTable
                emptyState={{
                  title: "No admitted evidence",
                  description:
                    "Artifacts appear here once a worker submits evidence and a verifier admits it against an accepted contract.",
                }}
                rows={canonicalRows}
                rowKey={(row) => row.key}
                columns={canonicalEvidenceColumns(onNavigate)}
              />
            </>
          )}
        </Section>
      ) : null}
      <Section
        title="Operational evidence projection"
        note="Compatibility records from the operational evidence store. They carry collector state and redaction controls, but a receipt cites the canonical artifacts above, not these."
        actions={
          <ScopeTabs
            value={state}
            onChange={setState}
            options={[
              { key: "all", label: `All (${catalog.length})`, actionId: EVIDENCE_FILTER_ALL },
              { key: "active", label: `Active (${activeCount})`, actionId: EVIDENCE_FILTER_ACTIVE },
              { key: "redacted", label: `Redacted (${redactedCount})`, actionId: EVIDENCE_FILTER_REDACTED },
              { key: "expired", label: `Expired (${expiredCount})`, actionId: EVIDENCE_FILTER_EXPIRED },
              { key: "deleted", label: `Deleted (${deletedCount})`, actionId: EVIDENCE_FILTER_DELETED },
            ]}
          />
        }
      >
        <LastFetched at={lastFetchedAt} stale={Boolean((artifactsApi.error || connectorsApi.error) && artifactsApi.data)} />
        <DataTable
          toolbar={
            <FilterToolbar>
              <FilterField label="Search" wide>
                <ListSearch
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search artifacts, kinds, hashes…"
                  aria-label="Search artifacts, kinds, hashes"
                />
              </FilterField>
            </FilterToolbar>
          }
          emptyState={{
            title: "No artifacts match",
            description: "Clear the filters to see the full library.",
          }}
          rows={rows}
          rowKey={(a) => a.id}
          onRowClick={(a) => onNavigate(href("evidence", a.id))}
          columns={evidenceColumns(onNavigate, queueCollection, (id) => {
            const source = connectorsApi.data?.items.find((row) => row.id === id);
            return source?.displayName ?? id;
          }, artifactsApi.data.provenance.demoScoped)}
        />
      </Section>
    </PageFrame>
  );
}

export function ArtifactDetailPage({ id, onNavigate }: VerificationDetailPageProps) {
  return (
    <MaybeDemo>
      <ArtifactDetailPageInner id={id} onNavigate={onNavigate} />
    </MaybeDemo>
  );
}

function ArtifactDetailPageInner({ id, onNavigate }: VerificationDetailPageProps) {
  const env = useVerificationEnvOptional();
  const environment = env?.environment ?? null;
  const [notice, setNotice] = useState<string | null>(null);
  const artifactApi = useApi(
    verificationCanonicalClients.artifactsGet.operation,
    environment ? { params: { id }, query: { env: environment } } : null,
  );
  const lineageApi = useApi(
    verificationCanonicalClients.lineageGet.operation,
    environment ? { params: { id }, query: { env: environment } } : null,
  );

  async function queueCollection() {
    if (!environment) return;
    setNotice(null);
    try {
      const result = await apiCall(verificationCanonicalClients.artifactsQueue.operation, {
        params: { id },
        query: { env: environment },
        body: { actorId: environmentActor(environment) },
      });
      setNotice(`Queued collection for ${result.artifact.label}.`);
      void artifactApi.mutate();
      void lineageApi.mutate();
    } catch (err) {
      setNotice(errorMessage(err));
    }
  }

  async function redactArtifact() {
    if (!environment) return;
    setNotice(null);
    try {
      const result = await apiCall(verificationCanonicalClients.artifactsRedact.operation, {
        params: { id },
        query: { env: environment },
        body: { actorId: environmentActor(environment), rationale: "Demo/dogfood redaction exercise from Evidence detail." },
      });
      setNotice(`Redacted ${result.artifact.label}.`);
      void artifactApi.mutate();
      void lineageApi.mutate();
    } catch (err) {
      setNotice(errorMessage(err));
    }
  }

  function downloadArtifact(kind: "manifest" | "content") {
    const a = artifactApi.data?.artifact;
    if (!a) return;
    const body = JSON.stringify({ kind, artifact: a, lineage: lineageApi.data?.entries ?? [] }, null, 2);
    triggerDownload(`${a.id.replace(/[^a-zA-Z0-9._-]/g, "_")}.${kind}.json`, body);
    setNotice(`Downloaded ${kind} for ${a.label}.`);
  }

  if (!environment) {
    return (
      <PageFrame page="artifact" wide eyebrow="Evidence" title="Artifact" lede={DETAIL_LEDE}>
        <PagePending title={`No artifact loaded for ${id}`}>
          Load the active Environment to open a seeded artifact.{" "}
          <PageLink onNavigate={onNavigate} to="evidence">
            Back to the evidence library
          </PageLink>
          .
        </PagePending>
      </PageFrame>
    );
  }

  if (artifactApi.error) {
    return (
      <PageFrame page="artifact" wide eyebrow="Evidence" title="Artifact" lede={DETAIL_LEDE}>
        <PageError title={`Artifact ${id} failed to load`} error={artifactApi.error} onRetry={() => void artifactApi.mutate()} />
      </PageFrame>
    );
  }

  if (artifactApi.isLoading || !artifactApi.data) {
    return (
      <PageFrame page="artifact" wide eyebrow="Evidence" title="Artifact" lede={DETAIL_LEDE}>
        <PagePending title="Loading artifact">Loading {environment === "demo" ? "Demo" : "Product dogfood"} artifact.</PagePending>
      </PageFrame>
    );
  }

  const a = artifactApi.data.artifact;
  const cited = a.citedByReceiptIds.length > 0;
  const contentAllowed = a.state !== "redacted";
  const redactBlocked = cited || a.state === "redacted" || a.state === "deleted";

  return (
    <PageFrame
      page="artifact"
      wide
      eyebrow={`Evidence artifact · ${a.sourceKind}`}
      title={a.label}
      lede={`Collected ${formatDateTime(a.collectedAt)} from ${a.sourceId}.`}
      actions={
        <>
          <ActionButton
            actionId={EVIDENCE_DOWNLOAD_ACTION}
            data-action-id={EVIDENCE_DOWNLOAD_ACTION}
            data-download-kind="manifest"
            data-artifact-id={a.id}
            onClick={() => downloadArtifact("manifest")}
          >
            Download manifest
          </ActionButton>
          <ActionButton
            actionId={EVIDENCE_DOWNLOAD_ACTION}
            data-action-id={EVIDENCE_DOWNLOAD_ACTION}
            data-download-kind="content"
            data-artifact-id={a.id}
            disabled={!contentAllowed}
            reason={contentAllowed ? undefined : "Content redacted; manifest remains downloadable"}
            onClick={() => downloadArtifact("content")}
          >
            Download content
          </ActionButton>
          <ActionButton
            actionId={EVIDENCE_REDACT_ACTION}
            data-action-id={EVIDENCE_REDACT_ACTION}
            variant={cited ? "outline" : "reject"}
            data-artifact-id={a.id}
            disabled={redactBlocked}
            reason={
              a.state === "redacted"
                ? "Already redacted"
                : a.state === "deleted"
                  ? "Deleted artifact content cannot be redacted again"
                : cited
                  ? `Cannot redact — cited by ${a.citedByReceiptIds.length} standing receipt(s)`
                  : undefined
            }
            onClick={redactArtifact}
          >
            Record redaction
          </ActionButton>
          <ActionButton
            actionId={EVIDENCE_QUEUE_COLLECTION_ACTION}
            data-action-id={EVIDENCE_QUEUE_COLLECTION_ACTION}
            data-artifact-id={a.id}
            onClick={queueCollection}
          >
            Queue collection
          </ActionButton>
        </>
      }
    >
      {notice ? <Callout tone={notice.includes("failed") ? "fail" : "accent"} title="Evidence action">{notice}</Callout> : null}
      {lineageApi.error ? (
        <PageError
          title="Lineage failed to load"
          error={lineageApi.error}
          onRetry={() => void lineageApi.mutate()}
          actionId="evidence.lineage-retry"
        />
      ) : null}
      <ButtonRow>
        <ToneBadge tone={stateTone(a.state)}>{a.state}</ToneBadge>
        {a.collectionQueuedAt ? (
          <ToneBadge tone="accent">{`collection queued ${formatRelativeTime(a.collectionQueuedAt)}`}</ToneBadge>
        ) : null}
      </ButtonRow>

      {cited && a.state !== "redacted" ? (
        <Callout tone="unknown" title="This artifact is load-bearing.">
          {a.citedByReceiptIds.length} standing receipt{a.citedByReceiptIds.length === 1 ? "" : "s"} cite it.{" "}
          {REDACT_BLOCKED_NOTE}
        </Callout>
      ) : null}

      {a.state !== "redacted" && a.state !== "deleted" ? (
        <Callout tone="neutral" title="What recording a redaction does">
          {REDACT_EFFECT_NOTE}
        </Callout>
      ) : null}

      {a.state === "redacted" ? (
        <Callout tone="neutral" title="Deletion state recorded; metadata retained">
          {REDACTED_STATE_NOTE} {REDACTED_REPLAY_NOTE}
        </Callout>
      ) : null}

      <Card>
        <CardContent>
          <Fields>
            <Field label="Artifact id">
              <Mono>{a.id}</Mono>
            </Field>
            <Field label="Content hash">
              <Mono>{a.contentHash}</Mono>
            </Field>
            <Field label="Size">{formatBytes(a.sizeBytes)}</Field>
            <Field label="Observations">
              <Mono>{a.observationCount}</Mono>
            </Field>
            <Field label="Source">{a.sourceId}</Field>
            {a.redactedAt ? <Field label="Redacted">{formatDateTime(a.redactedAt)}</Field> : null}
          </Fields>
        </CardContent>
      </Card>

      {lineageApi.data ? (
        <Section title="Lineage" note={`Head ${lineageApi.data.headEventHash}`}>
          <DataTable
            rows={lineageApi.data.entries}
            rowKey={(entry) => entry.id}
            columns={[
              { key: "kind", header: "Kind", render: (entry) => <ToneBadge tone="neutral">{entry.kind}</ToneBadge> },
              { key: "actor", header: "Actor", render: (entry) => <Mono>{entry.actorId}</Mono> },
              { key: "occurred", header: "Occurred", render: (entry) => formatDateTime(entry.occurredAt) },
              { key: "reason", header: "Reason", cellClassName: "whitespace-normal", render: (entry) => entry.reason },
            ]}
          />
        </Section>
      ) : null}

      <ButtonRow>
        <ActionButton actionId="nav.evidence" onClick={() => onNavigate(href("evidence"))}>Back to the library</ActionButton>
      </ButtonRow>
    </PageFrame>
  );
}

const CONNECTORS_LEDE =
  "Read-only connections to the systems that hold the truth. Every connector requests the narrowest scope that satisfies its controls, and none of them can write.";

function missingScopes(src: VerificationConnectorDto): string[] {
  return src.requiredScopes.filter((scope) => !src.grantedScopes.includes(scope));
}

function sourceCadence(src: VerificationConnectorDto): string {
  return `every ${formatNumber(Math.round(src.cadenceMinutes / 60))}h`;
}

function sourceFreshness(src: VerificationConnectorDto): string {
  if (!src.lastSuccessfulRunAt) return "Never collected";
  return formatRelativeTime(src.lastSuccessfulRunAt);
}

function sourceScopeSummary(src: VerificationConnectorDto): string {
  const missing = missingScopes(src);
  if (src.grantedScopes.length === 0) return "No scopes — credential revoked";
  if (missing.length > 0) return `Missing ${missing.join(", ")}`;
  return "All required scopes granted";
}

function sourceColumns(
  onNavigate: (to: string) => void,
  opts: { withReconnect: boolean; onReconnect: (src: VerificationConnectorDto) => void; demoScoped: boolean },
): DataTableColumn<VerificationConnectorDto>[] {
  const columns: DataTableColumn<VerificationConnectorDto>[] = [
    {
      key: "source",
      header: "Source",
      cellClassName: "whitespace-normal",
      render: (src) => (
        <div
          className="flex flex-col gap-0.5"
          data-demo-scoped={opts.demoScoped ? "true" : undefined}
        >
          <span className="font-medium text-foreground">{src.displayName}</span>
          <span className="text-muted-foreground">
            {providerLabel(src.provider)} · {sourceCadence(src)}
          </span>
        </div>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (src) => <ToneBadge tone={connectorTone(src.health)}>{src.health}</ToneBadge>,
    },
    {
      key: "freshness",
      header: "Freshness",
      cellClassName: "whitespace-normal",
      render: (src) => sourceFreshness(src),
    },
    {
      key: "scopes",
      header: "Scopes",
      cellClassName: "whitespace-normal",
      render: (src) => sourceScopeSummary(src),
    },
  ];
  if (opts.withReconnect) {
    columns.push({
      key: "action",
      header: "Action",
      render: (src) => (
        <ButtonRow>
          <ReconnectButton onNavigate={onNavigate} src={src} onReconnect={() => opts.onReconnect(src)} />
        </ButtonRow>
      ),
    });
  }
  return columns;
}

function OpenCatalogButton({
  onNavigate,
  disabled,
}: {
  onNavigate: (to: string) => void;
  disabled?: boolean;
}) {
  return (
    <ActionButton
      actionId={CONNECTORS_OPEN_CATALOG_ACTION}
      data-action-id={CONNECTORS_OPEN_CATALOG_ACTION}
      variant="primary"
      disabled={disabled}
      onClick={disabled ? undefined : () => onNavigate(CONNECTORS_CATALOG_HREF)}
    >
      Add a source
    </ActionButton>
  );
}

function ReconnectButton({
  onNavigate,
  src,
  onReconnect,
  disabled,
}: {
  onNavigate: (to: string) => void;
  src?: VerificationConnectorDto;
  onReconnect?: () => void;
  disabled?: boolean;
}) {
  const missing = src ? missingScopes(src) : [];
  const label =
    src && src.grantedScopes.length > 0 && missing.length > 0 ? "Grant missing scope" : "Reconnect";
  return (
    <ActionButton
      actionId={CONNECTORS_RECONNECT_ACTION}
      data-action-id={CONNECTORS_RECONNECT_ACTION}
      variant="primary"
      data-source-id={src?.id}
      data-provider={src?.provider}
      disabled={disabled}
      onClick={disabled ? undefined : (onReconnect ?? (() => onNavigate(CONNECTORS_CATALOG_HREF)))}
    >
      {label}
    </ActionButton>
  );
}

export function ConnectorsPage({ onNavigate }: VerificationPageProps) {
  return (
    <MaybeDemo>
      <ConnectorsPageInner onNavigate={onNavigate} />
    </MaybeDemo>
  );
}

function ConnectorsPageInner({ onNavigate }: VerificationPageProps) {
  const env = useVerificationEnvOptional();
  const environment = env?.environment ?? null;
  const ready = env?.ready ?? true;
  const [notice, setNotice] = useState<string | null>(null);
  const connectorsApi = useApi(
    verificationCanonicalClients.connectorsList.operation,
    ready && environment ? { query: { env: environment } } : null,
  );
  const lastFetchedAt = useLastFetchedAt(connectorsApi.data);

  async function reconnect(src: VerificationConnectorDto) {
    if (!environment) return;
    setNotice(null);
    try {
      const result = await apiCall(verificationCanonicalClients.connectorsReconnect.operation, {
        params: { id: src.id },
        query: { env: environment },
        body: { actorId: environmentActor(environment) },
      });
      setNotice(`Reconnected ${result.connector.displayName}.`);
      void connectorsApi.mutate();
    } catch (err) {
      setNotice(errorMessage(err));
    }
  }

  if (env && !ready) {
    return (
      <PageFrame page="connectors" wide eyebrow="Evidence" title="Connectors" lede={CONNECTORS_LEDE}>
        <PagePending title="Loading environment">Loading connectors for the active Environment.</PagePending>
      </PageFrame>
    );
  }

  if (!environment) {
    return (
      <PageFrame page="connectors" wide eyebrow="Evidence" title="Connectors" lede={CONNECTORS_LEDE}>
        <PagePending title="Environment unavailable">Connectors are scoped to the active Environment.</PagePending>
      </PageFrame>
    );
  }

  if (connectorsApi.error) {
    return (
      <PageFrame page="connectors" wide eyebrow="Evidence" title="Connectors" lede={CONNECTORS_LEDE}>
        <PageError title="Connectors failed to load" error={connectorsApi.error} onRetry={() => void connectorsApi.mutate()} />
      </PageFrame>
    );
  }

  if (connectorsApi.isLoading || !connectorsApi.data) {
    return (
      <PageFrame page="connectors" wide eyebrow="Evidence" title="Connectors" lede={CONNECTORS_LEDE}>
        <PagePending title="Loading connectors">Loading {environment === "demo" ? "Demo" : "Product dogfood"} sources.</PagePending>
      </PageFrame>
    );
  }

  const connectorsData = connectorsApi.data;
  const sources = connectorsData.items;
  const unhealthy = sources.filter((src) => src.health !== "healthy");
  const healthy = sources.filter((src) => src.health === "healthy");
  const collections = sources.reduce((sum, src) => sum + src.artifactCount, 0);

  return (
    <PageFrame
      page="connectors"
      wide
      eyebrow="Evidence"
      title="Connectors"
      lede={CONNECTORS_LEDE}
      actions={<OpenCatalogButton onNavigate={onNavigate} />}
    >
      <LastFetched at={lastFetchedAt} stale={Boolean(connectorsApi.error && connectorsApi.data)} />
      {notice ? <Callout tone={notice.includes("failed") ? "fail" : "accent"} title="Connector action">{notice}</Callout> : null}
      <StatGrid cols={3}>
        <Stat
          label="Sources"
          value={formatNumber(sources.length)}
          foot={`${formatNumber(healthy.length)} healthy · ${formatNumber(unhealthy.length)} needing attention`}
          actionId={unhealthy.length > 0 ? "connectors.jump-attention" : undefined}
          onClick={
            unhealthy.length > 0
              ? () => document.getElementById("connectors-attention")?.scrollIntoView({ behavior: "smooth", block: "start" })
              : undefined
          }
        />
        <Stat
          label="Collections in the window"
          value={formatNumber(collections)}
          foot="Snapshots these sources have taken"
        />
        <Stat
          label="Write capabilities held"
          value={0}
          tone="pass"
          foot="Every connector is read-only by construction"
        />
      </StatGrid>

      {sources.length === 0 ? (
        <PagePending title="No connectors configured">
          A stale source is why a control is UNKNOWN. Connectors land here with scope, cadence, and freshness.{" "}
          <PageLink onNavigate={onNavigate} to="evidence">
            Open the evidence library
          </PageLink>
          .
        </PagePending>
      ) : null}

      {unhealthy.length > 0 ? (
        <section id="connectors-attention">
          <DataTable
            title={`Needs attention · ${formatNumber(unhealthy.length)}`}
            subtitle="Listed first — a source problem is usually the real cause of an UNKNOWN."
            alwaysShowPager={false}
            pageSize={50}
            rows={unhealthy}
            rowKey={(src) => src.id}
            columns={sourceColumns(onNavigate, { withReconnect: true, onReconnect: reconnect, demoScoped: connectorsData.provenance.demoScoped })}
          />
        </section>
      ) : null}

      {healthy.length > 0 ? (
        <DataTable
          title={`Healthy · ${formatNumber(healthy.length)}`}
          subtitle="Inside their freshness window, with the scopes they need."
          alwaysShowPager={false}
          pageSize={50}
          rows={healthy}
          rowKey={(src) => src.id}
          columns={sourceColumns(onNavigate, { withReconnect: false, onReconnect: reconnect, demoScoped: connectorsData.provenance.demoScoped })}
        />
      ) : null}
    </PageFrame>
  );
}
